// Runtime browser-support detection for soft gating from app code (e.g.
// redirecting to /unsupported-browser). This mirrors the dependency-free guard
// injected into index.html by `unsupportedBrowserGuard` in vite.config.ts, so
// the hard (pre-React) and soft (in-app) gates agree. Both read their minimum
// versions from the single `SUPPORTED_BROWSERS` source of truth in the Vite
// config — here via the `__MIN_BROWSER_VERSIONS__` build-time define.

export type BrowserName = "chrome" | "edge" | "firefox" | "safari";

const MIN_VERSIONS = __MIN_BROWSER_VERSIONS__ as Record<BrowserName, number>;

export interface DetectedBrowser {
  /** `null` when the user agent isn't one we recognise. */
  name: BrowserName | null;
  /** Major version, or `null` when it couldn't be parsed. */
  version: number | null;
}

const major = (ua: string, re: RegExp): number | null => {
  const match = ua.match(re);
  return match ? parseInt(match[1], 10) : null;
};

export const detectBrowser = (
  ua: string = navigator.userAgent,
): DetectedBrowser => {
  // Order matters: Edge and Chrome-based browsers also contain "Chrome", and
  // Safari's UA contains "Safari" on Chrome too — check the most specific first.
  if (/Edg\//.test(ua))
    return { name: "edge", version: major(ua, /Edg\/(\d+)/) };
  if (/Firefox\//.test(ua))
    return { name: "firefox", version: major(ua, /Firefox\/(\d+)/) };
  if (/Chrome\//.test(ua))
    return { name: "chrome", version: major(ua, /Chrome\/(\d+)/) };
  if (/Version\/(\d+).*Safari/.test(ua))
    return { name: "safari", version: major(ua, /Version\/(\d+)/) };
  return { name: null, version: null };
};

/**
 * `true` when the browser meets the minimum supported version, or when it isn't
 * recognised (we don't block unknown browsers, to avoid false positives).
 */
export const isSupportedBrowser = (ua?: string): boolean => {
  const { name, version } = detectBrowser(ua);
  if (!name || version === null) return true;
  return version >= MIN_VERSIONS[name];
};

const INSTALL_URLS: Record<BrowserName, string> = {
  chrome: "https://www.google.com/chrome/",
  edge: "https://www.microsoft.com/edge/download",
  firefox: "https://www.mozilla.org/firefox/new/",
  // Safari ships with macOS; the closest "update" target is the support page.
  safari: "https://support.apple.com/en-us/102665",
};

const DISPLAY_NAMES: Record<BrowserName, string> = {
  chrome: "Chrome",
  edge: "Edge",
  firefox: "Firefox",
  safari: "Safari",
};

/**
 * Download/update page for the current browser, plus its display name, or
 * `null` when the browser isn't recognised.
 */
export const getBrowserUpgradeInfo = (
  ua?: string,
): { name: string; url: string } | null => {
  const { name } = detectBrowser(ua);
  if (!name) return null;
  return { name: DISPLAY_NAMES[name], url: INSTALL_URLS[name] };
};
